import { useEffect, useCallback, useRef, useState } from "react";

// ── Permission helper ─────────────────────────────────────────────────────────
// Requests permission exactly once (only when state is "default").
// Caches the result in localStorage so the prompt is never shown again.
async function requestPermissionOnce() {
  if (!("Notification" in window)) return "unsupported";
  if (Notification.permission !== "default") return Notification.permission;
  const result = await Notification.requestPermission();
  try {
    localStorage.setItem("sysnotif_permission", result);
  } catch (_) {}
  return result;
}

/**
 * Posts a notification payload to the Service Worker for display.
 * The SW handles:
 *   - showNotification() via self.registration (works when tab is backgrounded/minimized)
 *   - Tag-based dedup (same chatId = same notification, replaced)
 *   - Notification click → navigate to correct chat
 *
 * Falls back to `new Notification()` only if the SW controller is unavailable.
 */
function dispatchToServiceWorker(payload) {
  const { title, body, icon, data } = payload;

  // Prefer SW-based notification — reliable when tab is backgrounded
  if (navigator.serviceWorker?.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: "SHOW_NOTIFICATION",
      payload: { title, body, icon, data },
    });
    console.log("[NOTIF][HOOK] Dispatched SHOW_NOTIFICATION to Service Worker");
    return;
  }

  // Fallback: SW controller not yet available (first load, SW still installing)
  // Use navigator.serviceWorker.ready as a secondary path
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.showNotification(title, {
          body,
          icon,
          data,
          tag: data?.chatId || "chat-notification",
          renotify: true,
          requireInteraction: false,
          actions: [{ action: "open", title: "Open" }],
        });
        console.log(
          "[NOTIF][HOOK] Dispatched via SW registration.showNotification (controller was null)",
        );
      })
      .catch((err) => {
        console.warn(
          "[NOTIF][HOOK] SW ready fallback failed, using new Notification():",
          err.message,
        );
        fallbackNotification(title, body, icon);
      });
    return;
  }

  // Last resort: new Notification() — main-thread only, limited when backgrounded
  fallbackNotification(title, body, icon);
}

/**
 * Last-resort fallback using the main-thread Notification constructor.
 * Only used when the Service Worker is completely unavailable.
 */
function fallbackNotification(title, body, icon) {
  try {
    const n = new Notification(title, { body, icon });
    n.onclick = () => {
      window.focus();
      n.close();
    };
    console.log(
      "[NOTIF][HOOK] Fallback: new Notification() fired — title:",
      title,
    );
  } catch (err) {
    console.warn(
      "[NOTIF][HOOK] Fallback Notification creation failed:",
      err.message,
    );
  }
}

// ── Hook ──────────────────────────────────────────────────────────────────────
/**
 * useSystemNotifications
 *
 * Encapsulates all system-level notification logic:
 *   - Permission request (once on mount, non-blocking)
 *   - Deduplication via a bounded Set<messageId> (cap: 200)
 *   - Active-chat suppression via activeChatRef
 *   - Notification dispatch via Service Worker (NOT in-app UI)
 *   - Triggers push subscription when permission is granted
 *
 * @param {React.RefObject} activeChatRef  Stable ref to current activeChat
 * @param {{ subscribeToPush: Function }} pushHook  From usePushSubscription
 * @param {string|null} userId  Current user ID
 * @returns {{ showNotification, permissionState, requestPermission }}
 */
export function useSystemNotifications(
  activeChatRef,
  pushHook = null,
  userId = null,
) {
  // In-memory dedup set — stored in a ref so mutations never trigger re-renders
  const seenMessageIds = useRef(new Set());

  // Expose permission state so UI can react to it
  const [permissionState, setPermissionState] = useState(() => {
    if (!("Notification" in window)) return "unsupported";
    return Notification.permission;
  });

  // Request permission once on mount, non-blocking
  useEffect(() => {
    console.log(
      "[NOTIF][HOOK] Mounted — current Notification.permission:",
      "Notification" in window ? Notification.permission : "API unavailable",
    );
    requestPermissionOnce().then((result) => {
      console.log("[NOTIF][HOOK] requestPermissionOnce resolved:", result);
      setPermissionState(result);
      // If permission already granted on mount, subscribe to push
      if (result === "granted" && pushHook?.subscribeToPush && userId) {
        pushHook.subscribeToPush(userId);
      }
    });
    return () => {
      // Cleanup: free memory on unmount
      seenMessageIds.current.clear();
    };
  }, []); // runs exactly once — eslint-disable-line react-hooks/exhaustive-deps

  // Exposed so UI can trigger a re-request (only works if state is "default")
  const requestPermission = useCallback(async () => {
    if (!("Notification" in window)) return "unsupported";
    const result = await Notification.requestPermission();
    try {
      localStorage.setItem("sysnotif_permission", result);
    } catch (_) {}
    setPermissionState(result);
    console.log("[NOTIF][HOOK] Manual requestPermission resolved:", result);
    // Subscribe to push after user explicitly grants permission
    if (result === "granted" && pushHook?.subscribeToPush && userId) {
      pushHook.subscribeToPush(userId);
    }
    return result;
  }, [pushHook, userId]);

  // ── showNotification ──────────────────────────────────────────────────────
  const showNotification = useCallback((notif) => {
    const {
      messageId,
      chatType,
      chatId,
      senderName,
      channelName,
      messagePreview,
    } = notif || {};

    console.log("[NOTIF][HOOK] showNotification called:", {
      messageId,
      chatType,
      chatId,
      senderName,
    });

    // Guard 1: Browser support
    if (!("Notification" in window)) {
      console.warn(
        "[NOTIF][HOOK] BLOCKED — Notification API not supported in this browser",
      );
      return;
    }

    // Guard 2: Permission
    if (Notification.permission !== "granted") {
      console.warn(
        "[NOTIF][HOOK] BLOCKED — Notification.permission is:",
        Notification.permission,
        "(must be 'granted')",
      );
      return;
    }

    // Guard 3: Deduplication (In-memory + Cross-App LocalStorage)
    if (messageId) {
      if (seenMessageIds.current.has(messageId)) {
        console.log("[NOTIF][HOOK] BLOCKED — duplicate messageId (in-memory):", messageId);
        return;
      }
      seenMessageIds.current.add(messageId);
      if (seenMessageIds.current.size >= 200) {
        const oldest = seenMessageIds.current.values().next().value;
        seenMessageIds.current.delete(oldest);
      }
    }

    // Prepare notification variables before async processing
    const title =
      chatType === "channel"
        ? `${senderName || "Someone"} · #${channelName || "channel"}`
        : senderName || "New message";
    const bodyText = (messagePreview || "").slice(0, 100);
    const iconPath = "/sapient.png";
    const notifData = {
      chatId: chatId?.toString(),
      chatType: chatType || "direct",
      senderName: senderName || "Someone",
      channelName: channelName || "",
      messagePreview: bodyText,
      url: chatType === "channel" ? `/channel/${chatId}` : `/dm/${chatId}`,
    };

    const dispatchFinalNotification = () => {
      // Guard 4: Active-chat suppression
      const current = activeChatRef.current;
      const expectedType = chatType === "direct" ? "dm" : "channel";
      const currentId = (current?.data?._id || current?.data?.id)?.toString();

      if (
        current?.type === expectedType &&
        currentId != null &&
        currentId === chatId?.toString()
      ) {
        if (
          typeof document !== "undefined" &&
          document.visibilityState === "visible"
        ) {
          console.log("[NOTIF][HOOK] BLOCKED — user is viewing this chat in a visible tab");
          return;
        }
      }

      console.log("[NOTIF][HOOK] System notification dispatched ✅ — title:", title);
      dispatchToServiceWorker({ title, body: bodyText, icon: iconPath, data: notifData });
    };

    if (!messageId) {
      // Legacy path if messageId is somehow completely missing
      dispatchFinalNotification();
      return;
    }

    // Web Locks Cross-Tab Deduplication
    const processNotification = async () => {
      let alreadyNotified = false;

      const doDedup = () => {
        try {
          const notifiedTags = JSON.parse(
            localStorage.getItem("notifiedTags") || "[]"
          );
          if (notifiedTags.includes(messageId)) return true;
          notifiedTags.push(messageId);
          const pruned =
            notifiedTags.length > 200 ? notifiedTags.slice(-200) : notifiedTags;
          localStorage.setItem("notifiedTags", JSON.stringify(pruned));
          return false;
        } catch (err) {
          return false;
        }
      };

      if (navigator.locks) {
        await navigator.locks.request(`notif-${messageId}`, async () => {
          alreadyNotified = doDedup();
        });
      } else {
        alreadyNotified = doDedup();
      }

      if (alreadyNotified) {
        console.log("[NOTIF][HOOK] BLOCKED — duplicate messageId (cross-app localStorage):", messageId);
        return;
      }

      dispatchFinalNotification();
    };

    processNotification();
  }, []); // activeChatRef is a stable ref — no dep needed

  return { showNotification, permissionState, requestPermission };
}
